import { useDebounce } from '@anchorage/hooks';
/**
 * Manages the "add item" feature for sync Select — allows users to create
 * a new entry from their search term when no existing option matches.
 *
 * Handles: search term tracking + debounce, custom option injection into
 * the options list, OptionComponent override for the add-item row, and
 * forwarding search changes to the consumer callback.
 */
import type {
  ActionListOption,
  OptionComponentProps,
} from '@latitude/action-list';
import { useCallback, useMemo, useState } from 'react';
import { addItemOption } from 'utils';

import type { UseAddItemOptionsParams, UseAddItemOptionsReturn } from './types';

export const useAddItemOptions = <
  isMulti extends boolean = false,
  T = undefined,
>({
  options,
  isMulti,
  defaultSearchTerm,
  debounceSearchDelay,
  allowAddItem,
  addItemLabel,
  onSearchChange: onSearchChangeProp,
  OptionComponent: propsOptionComponent,
}: UseAddItemOptionsParams<isMulti, T>): UseAddItemOptionsReturn<T> => {
  const [searchTerm, setSearchTerm] = useState<string>(defaultSearchTerm);
  const debouncedSearchTerm = useDebounce(searchTerm, debounceSearchDelay);

  const showAddItemOption = !!(allowAddItem && debouncedSearchTerm && !isMulti);

  const resolvedOptions = useMemo(() => {
    if (!showAddItemOption) {
      return options;
    }

    const addItemCustomOption: ActionListOption<{ custom: true }> = {
      label: debouncedSearchTerm,
      value: debouncedSearchTerm,
      extraData: { custom: true },
    };

    return [...options, addItemCustomOption] as ActionListOption<T>[];
  }, [options, debouncedSearchTerm, showAddItemOption]);

  // Track search term internally when allowAddItem is active (needed to know
  // the label for the custom option), then forward to the consumer callback.
  // `open` is not available here — the parent hook provides it via closure.
  const onSearchChange = useCallback(
    (newSearchTerm?: string, open?: boolean) => {
      if (allowAddItem && !isMulti && open) {
        setSearchTerm(newSearchTerm || '');
      }
      onSearchChangeProp?.(newSearchTerm);
    },
    [allowAddItem, isMulti, onSearchChangeProp],
  );

  const OptionComponent = useMemo(() => {
    return showAddItemOption
      ? (addItemOption(debouncedSearchTerm, addItemLabel) as React.FC<
          OptionComponentProps<T>
        >)
      : propsOptionComponent;
  }, [
    showAddItemOption,
    debouncedSearchTerm,
    addItemLabel,
    propsOptionComponent,
  ]);

  return {
    resolvedOptions,
    debouncedSearchTerm,
    showAddItemOption,
    onSearchChange,
    OptionComponent,
  };
};
